'use client';
import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ComponentProps, RefObject } from 'react';

import { clampedTextVariants } from '@vs-shop/ui/clampedText';
import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The panel's short description: a few lines of the product's own copy, capped,
 * with a way through to the full text offered only when there is more to read.
 *
 *   <ProductPanelDescription>
 *     <ProductPanelDescriptionText>{shortDescription}</ProductPanelDescriptionText>
 *     <ProductPanelDescriptionMore>
 *       <a href="#description">{label}</a>
 *     </ProductPanelDescriptionMore>
 *   </ProductPanelDescription>
 *
 * **Why this block is a client leaf and the rest of the panel is not.** Whether
 * the copy overflows its cap cannot be known before it is laid out: it depends
 * on the panel's width, on the reader's font size, and on the copy itself. So
 * the block measures its own text and the "read the rest" affordance is
 * revealed rather than rendered. Nothing is offered on the first paint, which
 * is the correct state for a server render: there is no layout to measure yet.
 *
 * The three parts are still open anatomy, and the tenant still writes the copy
 * and the affordance: the block passes its measurement down as context rather
 * than taking either as a prop. `'use client'` therefore sits on this file
 * alone; the copy and the link a tenant composes into it are rendered on the
 * server and arrive as children.
 */

/**
 * What the block knows and its parts need: where to measure, and whether there
 * was anything left over.
 *
 * Not exported. It is anatomy below the line a tenant writes — every consumer
 * is a part in this file, and what a tenant composes is the part.
 */
type ProductPanelDescriptionState = {
  readonly textRef: RefObject<HTMLParagraphElement | null>;
  readonly isClamped: boolean;
};

const ProductPanelDescriptionContext = createContext<ProductPanelDescriptionState | null>(null);

/**
 * Throws rather than degrading: a text part outside the block would silently
 * never be measured, and an affordance outside it would silently never appear.
 * Both read as "the copy is short" and neither is visible in review.
 */
const useProductPanelDescription = (part: string): ProductPanelDescriptionState => {
  const state = useContext(ProductPanelDescriptionContext);
  if (state === null) {
    throw new Error(`<${part}> must be composed inside <ProductPanelDescription>.`);
  }
  return state;
};

export type ProductPanelDescriptionProps = ComponentProps<'div'>;

/**
 * The block. Carries no recipe: the step between it and its neighbours belongs
 * to the panel, and the copy's own type belongs to the text part inside it.
 */
export const ProductPanelDescription = ({
  className,
  children,
  ...props
}: ProductPanelDescriptionProps) => {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);

  const measure = (element: HTMLParagraphElement) => {
    setIsClamped(element.scrollHeight > element.clientHeight);
  };

  // Before the first paint, so an affordance that IS warranted does not appear
  // a frame late over copy the reader has already started on.
  useLayoutEffect(() => {
    const element = textRef.current;
    if (element === null) return;
    measure(element);
  }, [children]);

  // And after every reflow: the panel is a column that narrows with the
  // viewport, and copy that fits at one width overflows at the next.
  useEffect(() => {
    const element = textRef.current;
    if (element === null) return;

    const observer = new ResizeObserver(() => {
      measure(element);
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
    // The element is a stable ref, so it is observed once for the block's
    // lifetime; a content change is measured by the layout effect above.
  }, []);

  const state = useMemo(() => ({ textRef, isClamped }), [isClamped]);

  return (
    <div
      data-slot="product-panel-description"
      className={optionalClassName(clsxm(className))}
      {...props}
    >
      <ProductPanelDescriptionContext.Provider value={state}>
        {children}
      </ProductPanelDescriptionContext.Provider>
    </div>
  );
};

/**
 * The copy itself, capped at six lines.
 *
 * The cap is the block's own rather than a prop: how much of a product's
 * description belongs in the panel beside its buy path is the section's
 * judgement, and a caller free to raise it could push the price below the fold.
 * The clamp is `@vs-shop/ui`'s recipe rather than a second one written here;
 * the element is a paragraph, because that is what a paragraph of product copy
 * is to a reader and to a crawler.
 */
const productPanelDescriptionTextClasses = [
  clampedTextVariants({ lines: 6 }),
  'text-foreground-muted text-base/6 tracking-tight',
];

export type ProductPanelDescriptionTextProps = ComponentProps<'p'>;

/** The capped copy. This is the element the block measures. */
export const ProductPanelDescriptionText = ({
  className,
  ...props
}: ProductPanelDescriptionTextProps) => {
  const { textRef } = useProductPanelDescription('ProductPanelDescriptionText');

  return (
    <p
      data-slot="product-panel-description-text"
      ref={textRef}
      className={clsxm(productPanelDescriptionTextClasses, className)}
      {...props}
    />
  );
};

/**
 * The way through to the full text, shown only once there is text the cap is
 * hiding. What it says and where it leads are the tenant's, and arrive as
 * children rendered on the server.
 */
const productPanelDescriptionMoreClasses = 'mt-1 inline-block';

export type ProductPanelDescriptionMoreProps = ComponentProps<'div'>;

/** The "read the rest" affordance. Renders nothing while the copy fits. */
export const ProductPanelDescriptionMore = ({
  className,
  ...props
}: ProductPanelDescriptionMoreProps) => {
  const { isClamped } = useProductPanelDescription('ProductPanelDescriptionMore');

  if (!isClamped) return null;

  return (
    <div
      data-slot="product-panel-description-more"
      className={clsxm(productPanelDescriptionMoreClasses, className)}
      {...props}
    />
  );
};
